// src/utils/userLogger.js
// Frontend-only user logging utility

class UserLogger {
  constructor() {
    this.logKey = 'originselect_user_logs';
    this.shareLogKey = 'originselect_share_logs';
  }

  // Log user sign-in
  logSignIn(userInfo) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action: 'signin',
      email: userInfo.email,
      name: userInfo.name,
      sessionId: this.generateSessionId()
    };
    
    this.addLogEntry(logEntry);
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 User Sign-In:', logEntry);
    }
  }

  // Log user sign-out
  logSignOut(email) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action: 'signout',
      email: email,
      sessionId: this.getCurrentSessionId()
    };
    
    this.addLogEntry(logEntry);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🚪 User Sign-Out:', logEntry);
    }
  }

  // Log product share events
  logProductShare(shareData) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action: 'share',
      productId: shareData.productId,
      productName: shareData.productName,
      shareMethod: shareData.shareMethod, // 'native', 'copy_link', 'facebook', etc.
      userEmail: shareData.userEmail || 'anonymous',
      productUrl: shareData.productUrl,
      sessionId: this.getCurrentSessionId()
    };
    
    this.addShareLogEntry(logEntry);
    
    // Console logging with emoji for easy identification
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 Product Share:', logEntry);
    }
  }

  // Get all user logs
  getUserLogs() {
    const logs = localStorage.getItem(this.logKey);
    return logs ? JSON.parse(logs) : [];
  }

  // Get all share logs
  getShareLogs() {
    const logs = localStorage.getItem(this.shareLogKey);
    return logs ? JSON.parse(logs) : [];
  }

  // Get unique users who have signed in
  getUniqueUsers() {
    const logs = this.getUserLogs();
    const uniqueEmails = new Set();
    
    logs.forEach(log => {
      if (log.action === 'signin' && log.email) {
        uniqueEmails.add(log.email);
      }
    });
    
    return Array.from(uniqueEmails);
  }

  // Get share statistics
  getShareStats() {
    const shareLogs = this.getShareLogs();
    const stats = {
      totalShares: shareLogs.length,
      uniqueProducts: new Set(shareLogs.map(log => log.productId)).size,
      sharesByMethod: {},
      mostSharedProducts: {},
      sharesByUser: {}
    };

    shareLogs.forEach(log => {
      // Count by method
      stats.sharesByMethod[log.shareMethod] = (stats.sharesByMethod[log.shareMethod] || 0) + 1;
      
      // Count by product
      stats.mostSharedProducts[log.productName] = (stats.mostSharedProducts[log.productName] || 0) + 1;
      
      // Count by user (if not anonymous)
      if (log.userEmail && log.userEmail !== 'anonymous') {
        stats.sharesByUser[log.userEmail] = (stats.sharesByUser[log.userEmail] || 0) + 1;
      }
    });

    return stats;
  }

  // Export all logs (both user and share logs)
  exportAllLogs() {
    const userLogs = this.getUserLogs();
    const shareLogs = this.getShareLogs();
    const exportData = {
      userLogs,
      shareLogs,
      exportTimestamp: new Date().toISOString(),
      summary: {
        totalUsers: this.getUniqueUsers().length,
        totalShares: shareLogs.length,
        shareStats: this.getShareStats()
      }
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `originselect-all-logs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  }

  // Export logs as downloadable JSON
  exportLogs() {
    const logs = this.getUserLogs();
    const dataStr = JSON.stringify(logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `originselect-user-logs-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  }

  // Private methods
  addLogEntry(entry) {
    const logs = this.getUserLogs();
    logs.push(entry);
    
    // Keep only last 1000 entries to prevent localStorage bloat
    if (logs.length > 1000) {
      logs.splice(0, logs.length - 1000);
    }
    
    localStorage.setItem(this.logKey, JSON.stringify(logs));
  }

  addShareLogEntry(entry) {
    const logs = this.getShareLogs();
    logs.push(entry);
    
    // Keep only last 1000 entries to prevent localStorage bloat
    if (logs.length > 1000) {
      logs.splice(0, logs.length - 1000);
    }
    
    localStorage.setItem(this.shareLogKey, JSON.stringify(logs));
  }

  generateSessionId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  getCurrentSessionId() {
    return sessionStorage.getItem('originselect_session_id') || 'unknown';
  }
}

export const userLogger = new UserLogger();
